import { Injectable } from '@angular/core';
import { shareOneReplay } from '@examdojo/rxjs';
import { QueryEntity } from '@examdojo/state';
import { isNotNullish } from '@examdojo/util/nullish';
import { combineLatest, debounceTime, distinctUntilChanged, filter, map, Observable } from 'rxjs';
import { CourseLevel, CourseStoreModel, Program, ProgramMetadata, PROGRAMS } from '../model';
import { SyllabusAndCourseLevel, SyllabusStoreModel, SyllabusUIModel } from './syllabus.model';
import { SyllabusState, SyllabusStore } from './syllabus.store';

@Injectable({ providedIn: 'root' })
export class SyllabusQuery extends QueryEntity<SyllabusState, SyllabusStoreModel, SyllabusUIModel, 'id', number> {
  constructor(protected override store: SyllabusStore) {
    super(store);
  }

  readonly availablePrograms$ = this.entities$.pipe(
    map((entities) =>
      PROGRAMS.filter((program) => !!entities.find((entity) => entity.program.code === program && entity.active)),
    ),
  );

  readonly programsMetadata$: Observable<ProgramMetadata[]> = this.entities$.pipe(
    map((entities) => {
      const programToMetadata = new Map<Program, ProgramMetadata>();
      entities.forEach((syllabus) => {
        programToMetadata.set(syllabus.program.code, syllabus.program);
      });

      return Array.from(programToMetadata.values());
    }),
    shareOneReplay(),
  );

  readonly activeProgram$ = this.active$.pipe(map((active) => active?.program));

  readonly markCalculationMethod$ = this.active$.pipe(map((active) => active?.mark_calculation_method ?? 'ADDITIVE'));

  readonly courseLevel$ = this.selectProp('course_level');

  readonly activeIdAndCourseLevel$: Observable<SyllabusAndCourseLevel> = combineLatest([
    this.selectActiveId().pipe(distinctUntilChanged()),
    this.courseLevel$.pipe(distinctUntilChanged()),
  ]).pipe(
    filter((arr): arr is [SyllabusStoreModel['id'], CourseLevel | null] => {
      const [syllabusId, courseLevel] = arr;
      return isNotNullish(syllabusId) && courseLevel !== undefined;
    }),
    debounceTime(0), // Both value are set at the same time in the store, so we take them in one shot
    map(([syllabusId, courseLevel]) => ({ syllabusId, courseLevel })),
  );

  readonly courses$ = this.selectProp('courses').pipe(map((courses) => courses ?? []));

  readonly isMarkCalculationMethodSelective$ = this.selectActive().pipe(
    map((active) => this.getIsMarkCalculationMethodSelective(active)),
  );

  readonly activeCourse$ = combineLatest([this.courses$, this.activeIdAndCourseLevel$]).pipe(
    map(([courses, { syllabusId, courseLevel }]) =>
      courses.find((course) => course.syllabus_id === syllabusId && course.course_level === courseLevel),
    ),
  );

  readonly activeFormularBooklet$ = this.activeCourse$.pipe(map((course) => course?.formular_booklet ?? null));

  getIsMarkCalculationMethodSelective(syllabus: SyllabusStoreModel | null = this.getActive()) {
    return syllabus?.mark_calculation_method === 'SELECTIVE';
  }

  /**
   * Get syllabus by slug
   */
  getBySlug(slug: string): SyllabusUIModel | undefined {
    return Object.values(this.getValue().entities).find((syllabus: SyllabusStoreModel) => syllabus.slug === slug);
  }

  /**
   * Get the slug of the currently active syllabus
   */
  selectActiveSlug(): Observable<string | null> {
    return this.selectActive().pipe(map((syllabus: SyllabusStoreModel | null) => syllabus?.slug ?? null));
  }

  getCourseBySlug(slug: string): CourseStoreModel | undefined {
    return this.getProp('courses')?.find((course) => course.course_slug === slug);
  }

  getLabel(syllabusUiModel: SyllabusUIModel, courseLevel?: CourseLevel | null) {
    const syllabusName = syllabusUiModel.name;
    if (!courseLevel) {
      return syllabusName;
    }
    return `${syllabusName!} ${courseLevel}`;
  }
}
